import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Animation presets that can be reused
export const animationPresets = {
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1, duration: 1 }
  },
  fadeInUp: {
    from: { opacity: 0, y: 50 },
    to: { opacity: 1, y: 0, duration: 1 }
  },
  fadeInDown: {
    from: { opacity: 0, y: -50 },
    to: { opacity: 1, y: 0, duration: 1 }
  },
  zoomIn: {
    from: { opacity: 0, scale: 0.5 },
    to: { opacity: 1, scale: 1, duration: 1 }
  },
  slideInLeft: {
    from: { opacity: 0, x: -50 },
    to: { opacity: 1, x: 0, duration: 1 }
  },
  slideInRight: {
    from: { opacity: 0, x: 50 },
    to: { opacity: 1, x: 0, duration: 1 }
  }
};

// ScrollTrigger defaults that can be overridden
export const defaultScrollTriggerOptions = {
  start: 'top 80%',
  end: 'bottom 20%',
  toggleActions: 'play none none reverse'
};

export function useGSAP(animations) {
  useEffect(() => {
    const contexts = [];

    // Apply each animation configuration
    animations.forEach(({ ref, preset, options = {}, scrollTrigger = {} }) => {
      if (!ref.current) return;

      const context = gsap.context(() => {
        const animation = gsap.fromTo(
          ref.current,
          {
            ...animationPresets[preset].from,
            ...options.from
          },
          {
            ...animationPresets[preset].to,
            ...options.to,
            scrollTrigger: {
              trigger: ref.current,
              ...defaultScrollTriggerOptions,
              ...scrollTrigger
            }
          }
        );

        return animation;
      });

      contexts.push(context);
    });

    // Cleanup function
    return () => {
      contexts.forEach(context => context.revert());
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [animations]);
}

// Helper function for staggered animations
export function createStaggeredAnimation(
  elements,
  preset,
  staggerOptions = {}
) {
  const defaults = {
    stagger: 0.2,
    ...animationPresets[preset]
  };

  return gsap.fromTo(
    elements,
    {
      ...defaults.from,
      ...staggerOptions.from
    },
    {
      ...defaults.to,
      ...staggerOptions.to,
      stagger: staggerOptions.stagger || defaults.stagger
    }
  );
}
